/* Image utilities — compress large phone photos before sending to AI.
 *
 * Phone cameras shoot 4-8 MB photos. Sending them as base64 JSON balloons
 * to 6-11 MB and times out on most proxies. We resize + re-encode in the
 * browser before upload.
 */

const MAX_WIDTH = 1280; // Claude vision works fine up to ~1024 longest edge
const QUALITY = 0.82;

/**
 * Read a File and return a compressed base64 data URL.
 * Falls back to original data URL if compression fails (e.g. SVG/HEIC).
 */
/**
 * Read a File, center-crop to a square focused on the middle of the frame
 * (so the AI ignores plants at the edges / leaves on the ground), then
 * compress. Returns a base64 JPEG data URL.
 */
export function fileToCenterCroppedDataURL(file, opts = {}) {
    const cropFactor = opts.cropFactor || 0.78; // keep the central ~78% of the shorter side
    const maxWidth = opts.maxWidth || 1024;
    const quality = opts.quality || QUALITY;
    return new Promise((resolve, reject) => {
        if (!file) { reject(new Error('Ingen fil')); return; }
        const reader = new FileReader();
        reader.onerror = () => reject(new Error('Kunne ikke læse filen'));
        reader.onload = () => {
            const raw = reader.result;
            if (typeof raw !== 'string' || !raw.startsWith('data:image/')) { resolve(raw); return; }
            const img = new Image();
            img.onerror = () => resolve(raw);
            img.onload = () => {
                try {
                    const side = Math.round(Math.min(img.width, img.height) * cropFactor);
                    const sx = Math.round((img.width - side) / 2);
                    const sy = Math.round((img.height - side) / 2);
                    const outSide = Math.min(side, maxWidth);
                    const canvas = document.createElement('canvas');
                    canvas.width = outSide;
                    canvas.height = outSide;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, sx, sy, side, side, 0, 0, outSide, outSide);
                    resolve(canvas.toDataURL('image/jpeg', quality));
                } catch (e) {
                    console.error('[image] crop failed, using raw', e);
                    resolve(raw);
                }
            };
            img.src = raw;
        };
        reader.readAsDataURL(file);
    });
}

export function fileToCompressedDataURL(file, opts = {}) {
    const maxWidth = opts.maxWidth || MAX_WIDTH;
    const quality = opts.quality || QUALITY;
    return new Promise((resolve, reject) => {
        if (!file) {
            reject(new Error('Ingen fil'));
            return;
        }
        const reader = new FileReader();
        reader.onerror = () => reject(new Error('Kunne ikke læse filen'));
        reader.onload = () => {
            const raw = reader.result;
            // SVG / unknown types — pass through unchanged
            if (typeof raw !== 'string' || !raw.startsWith('data:image/')) {
                resolve(raw);
                return;
            }
            const img = new Image();
            img.onerror = () => {
                // If image can't decode, fall back to raw (e.g. HEIC on some browsers)
                resolve(raw);
            };
            img.onload = () => {
                try {
                    const scale = Math.min(1, maxWidth / Math.max(img.width, img.height));
                    const w = Math.round(img.width * scale);
                    const h = Math.round(img.height * scale);
                    const canvas = document.createElement('canvas');
                    canvas.width = w;
                    canvas.height = h;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0, w, h);
                    // Always re-encode as JPEG (no transparency needed, much smaller)
                    const out = canvas.toDataURL('image/jpeg', quality);
                    resolve(out);
                } catch (e) {
                    console.error('[image] compress failed, using raw', e);
                    resolve(raw);
                }
            };
            img.src = raw;
        };
        reader.readAsDataURL(file);
    });
}

export function dataURLToFile(dataUrl, name = 'plant.jpg') {
    const [meta, encoded] = dataUrl.split(',');
    const mime = (meta.match(/data:(.*?);/) || [])[1] || 'image/jpeg';
    const binary = atob(encoded);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    return new File([bytes], name, { type: mime });
}
